import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsTrackerProps {
  pageTitle?: string;
}

const AnalyticsTracker = ({ pageTitle }: AnalyticsTrackerProps) => {
  const sessionIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const hasTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    // Generate or get session ID
    if (!sessionIdRef.current) {
      sessionIdRef.current = sessionStorage.getItem('bt_session_id') || 
        `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('bt_session_id', sessionIdRef.current);
    }

    // Track page view
    const trackPageView = async () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      try {
        const analyticsData = {
          sessionId: sessionIdRef.current,
          pageUrl: window.location.href,
          pageTitle: pageTitle || document.title || 'Unknown Page',
          referrer: document.referrer || null,
          visitDuration: 0
        };

        console.log('🔍 Tracking page view:', analyticsData);

        const { data, error } = await supabase.functions.invoke('site_analytics_20251219_1900', {
          body: analyticsData
        });

        if (error) {
          console.error('❌ Analytics tracking error:', error);
          
          // Fallback: try direct database insert
          console.log('🔄 Trying direct database insert...');
          const fallbackData = {
            session_id: sessionIdRef.current,
            ip_address: 'unknown',
            user_agent: navigator.userAgent,
            page_url: window.location.href,
            page_title: pageTitle || document.title || 'Unknown Page',
            referrer: document.referrer || null,
            device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other',
            os: navigator.platform || 'Unknown',
            visit_duration: 0,
            is_unique_visitor: true,
            is_returning_visitor: false
          };
          
          const { error: dbError } = await supabase
            .from('site_analytics_20251219_1900')
            .insert([fallbackData]);
            
          if (dbError) {
            console.error('❌ Direct database insert also failed:', dbError);
          } else {
            console.log('✅ Direct database insert successful');
          }
        } else {
          console.log('✅ Analytics tracked successfully:', data);
        }
      } catch (error) {
        console.error('Analytics tracking failed:', error);
      }
    };

    // Track page view after a short delay to ensure page is loaded
    const trackingTimeout = setTimeout(trackPageView, 1000);

    // Track page exit
    const trackPageExit = async () => {
      const visitDuration = Math.round((Date.now() - startTimeRef.current) / 1000);
      
      if (visitDuration > 5) { // Only track if user stayed more than 5 seconds
        try {
          const analyticsData = {
            sessionId: sessionIdRef.current,
            pageUrl: window.location.href,
            pageTitle: pageTitle || document.title || 'Unknown Page',
            referrer: document.referrer || null,
            visitDuration
          };

          // Use sendBeacon for reliable tracking on page exit
          if (navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify(analyticsData)], {
              type: 'application/json'
            });
            navigator.sendBeacon('/api/analytics', blob);
          } else {
            // Fallback for browsers without sendBeacon
            await supabase.functions.invoke('site_analytics_20251219_1900', {
              body: analyticsData
            });
          }
        } catch (error) {
          console.error('Exit tracking failed:', error);
        }
      }
    };

    // Add event listeners for page exit
    const handleBeforeUnload = () => trackPageExit();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackPageExit();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearTimeout(trackingTimeout);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pageTitle]);

  // This component doesn't render anything
  return null;
};

export default AnalyticsTracker;